import UserProfile from '../Common/UserProfile';
import Api from './Api';
import { store } from '../../store/store';
import {
  setOpenJobsCount,
  setLoadingJobs,
  setOpenRequestsCount,
  setLoadingRequests,
  setUser,
  setPlanInfo,
  setAccountInfo,
  setCompanyTree,
  setLoadingCompanyTree,
  setApplicantTags,
  setJobTags,
  setIdentityDocs,
  setIndustries,
  setLinkedinIndustries
} from '../../store/actions/actions';
import { ensureCountriesLoaded } from './countriesCache';
import { ensureValidSession, isSessionEnding } from './authSession';

let initializePromise = null;
let applicantTagsInflight = null;
let jobTagsInflight = null;
let companyTreeInflight = null;
let applicantTagsCache = null;
let jobTagsCache = null;
const applicantTagsById = new Map();

function apiGet(path, filter) {
  return new Promise((resolve) => {
    const api = new Api();
    api.get(path, filter || {}, (status, data) => {
      resolve({ status, data });
    });
  });
}

async function getOpenJobsCount() {
  store.dispatch(setLoadingJobs());

  const count = await new Promise((resolve) => {
    const api = new Api();
    api.getCount('Jobs', { status: 'PUBLISHED', deleted: false }, (status, data) => {
      resolve(
        status >= 200 && status < 400 && data && typeof data.count === 'number' ? data.count : 0
      );
    });
  });
  store.dispatch(setOpenJobsCount(count));

  return count;
}

async function getOpenRequestsCount() {
  store.dispatch(setLoadingRequests());

  const filter = {
    fields: ['id'],
    where: {
      status: { inq: ['APPROVED', 'WAITING_APPROVAL', 'REQUEST'] }
    }
  };
  const { data: visibleReqs } = await apiGet('JobRequests', filter);
  const count = Array.isArray(visibleReqs) ? visibleReqs.length : 0;
  store.dispatch(setOpenRequestsCount(count));
  return count;
}

async function getPlanInfo() {
  const api = new Api();

  const planInfo = await api.getParamAsync('Accounts/plan-info', {});
  store.dispatch(setPlanInfo(planInfo.data));
  return planInfo.data;
}

async function getAccountInfo() {
  const filter = {
    fields: [
      'account_id',
      'unique_name',
      'name',
      'industry',
      'abbreviation',
      'country',
      'integration_info',
      'config_params',
      'crossjob_active',
      'webhook_hire_config',
      'blocklist_active',
      'external_integration_info',
      'app_version',
      'domains'
    ],
    where: {
      account_id: UserProfile.getRealm()
    }
  };
  const { status, data: accounts } = await apiGet('Accounts', filter);
  if (status >= 200 && status < 400 && Array.isArray(accounts) && accounts.length > 0) {
    store.dispatch(setAccountInfo(accounts[0]));
    return accounts[0];
  }

  return null;
}

async function getCompanyTree(options = {}) {
  const force = options.force === true;
  const existing = store.getState().companyTree.flatData;
  if (!force && Array.isArray(existing) && existing.length > 0) {
    return existing;
  }

  if (companyTreeInflight) return companyTreeInflight;

  companyTreeInflight = (async () => {
    store.dispatch(setLoadingCompanyTree());

    const filter = {
      order: ['name']
    };
    const { data: companyTree } = await apiGet('CompanyTree', filter);
    store.dispatch(setCompanyTree(companyTree || []));
    return companyTree || [];
  })().finally(() => {
    companyTreeInflight = null;
  });

  return companyTreeInflight;
}

async function getCountries() {
  try {
    await ensureCountriesLoaded();
  } catch (error) {
    console.error('Error preloading countries list', error);
  }
}

async function getUser() {
  if (store.getState().user.user.username) {
    return store.getState().user.user;
  }

  const userId = UserProfile.getId();
  const { status, data } = await apiGet('AccountUsers/' + userId, {});
  if (status >= 200 && status < 400 && data && typeof data === 'object' && !Array.isArray(data)) {
    store.dispatch(setUser(data));
    return data;
  }

  return null;
}

async function refreshUser() {
  const userId = UserProfile.getId();
  const { status, data } = await apiGet('AccountUsers/' + userId, {});
  if (status >= 200 && status < 400 && data && typeof data === 'object' && !Array.isArray(data)) {
    store.dispatch(setUser(data));
    return data;
  }

  return null;
}

/**
 * Loads applicant tags once. Concurrent callers share the same in-flight request.
 * Pass { force: true } after create/update to bypass the Redux cache.
 */
async function getApplicantTags(options = {}) {
  const force = options.force === true;
  if (!force && applicantTagsCache) return applicantTagsCache;

  const existing = store.getState().applicantTags.tags;
  if (!force && Array.isArray(existing) && existing.length > 0) {
    applicantTagsCache = existing;
    return existing;
  }

  if (applicantTagsInflight) return applicantTagsInflight;

  applicantTagsInflight = (async () => {
    const filter = {
      fields: ['id', 'name', 'active', 'color'],
      where: {
        realm: UserProfile.getRealm(),
        deleted: false
      },
      order: ['name']
    };
    const { status, data } = await apiGet('ApplicantTags', filter);
    if (status >= 200) {
      applicantTagsCache = data;
      if (Array.isArray(data)) {
        for (let i = 0; i < data.length; i++) {
          const tag = data[i];
          if (tag && tag.id != null) applicantTagsById.set(tag.id, tag);
        }
      }

      store.dispatch(setApplicantTags(data));
      return data;
    }

    console.log('Error on fetching ApplicantTags');
    return existing || [];
  })().finally(() => {
    applicantTagsInflight = null;
  });

  return applicantTagsInflight;
}

async function getJobTags(options = {}) {
  const force = options.force === true;
  if (!force && jobTagsCache) return jobTagsCache;

  const existing = store.getState().jobTags.tags;
  if (!force && Array.isArray(existing) && existing.length > 0) {
    jobTagsCache = existing;
    return existing;
  }

  if (jobTagsInflight) return jobTagsInflight;

  jobTagsInflight = (async () => {
    const filter = {
      fields: ['id', 'name', 'active', 'color'],
      where: {
        realm: UserProfile.getRealm(),
        deleted: false
      },
      order: ['name']
    };
    const { status, data } = await apiGet('JobTags', filter);
    if (status >= 200) {
      jobTagsCache = data;
      store.dispatch(setJobTags(data));
      return data;
    }

    console.log('Error on fetching JobTags');
    return existing || [];
  })().finally(() => {
    jobTagsInflight = null;
  });

  return jobTagsInflight;
}

async function getIdentityDocs() {
  const filter = {
    order: ['code DESC', 'country']
  };
  const { status, data } = await apiGet('IdentityDocuments', filter);
  if (status >= 200) {
    store.dispatch(setIdentityDocs(data));
    return data;
  }

  console.log('Error on fetching IdentityDocuments');
  return [];
}

async function getIndustries() {
  const userState = store.getState().user.user;
  const language = (userState && userState.language) || navigator.language || 'pt-br';

  const filter = {
    where: {
      language: String(language).substr(0, 2)
    }
  };
  const { status, data } = await apiGet('Industries', filter);
  if (status >= 200) {
    store.dispatch(setIndustries(data));
    return data;
  }

  console.log('Error on fetching Industries');
  return [];
}

async function getLinkedinIndustries() {
  const { status, data } = await apiGet('IndustryMappingLinkedIn', {});
  if (status >= 200) {
    store.dispatch(setLinkedinIndustries(data));
    return data;
  }

  console.log('Error on fetching IndustryMappingLinkedIn');
  return [];
}

/**
 * Non-critical shell data after the first paint path is free.
 * Tags stay on-demand (ensureApplicantTags / fetchApplicantTagsByIds / ensureJobTags).
 */
function scheduleDeferredBootstrap() {
  const run = () => {
    if (isSessionEnding() || !UserProfile.hasLogged()) return;

    getOpenJobsCount();
    getOpenRequestsCount();

    getIdentityDocs();
    getIndustries();
    getLinkedinIndustries();
    getCountries();

    try {
      const lang =
        (store.getState().user.user && store.getState().user.user.language) ||
        (typeof navigator !== 'undefined' && navigator.language) ||
        'pt-br';
      import('../Jobs/JobRequirements/JobRequirementsRepository.js')
        .then((mod) => mod.prefetchJobRequirementCatalogs(lang))
        .catch(() => {});
    } catch (e) {
      /* ignore */
    }
  };

  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(run, { timeout: 4000 });
  } else {
    setTimeout(run, 1500);
  }
}

/**
 * App shell bootstrap: session first, then account shell, then idle work.
 * Applicant/Job tags are never warmed here — callers use ensure* / fetchByIds.
 */
const initialize = async () => {
  if (initializePromise) return initializePromise;

  initializePromise = (async () => {
    await ensureValidSession();
    if (isSessionEnding()) return;

    await Promise.all([getUser(), getAccountInfo(), getPlanInfo()]);
    if (isSessionEnding()) return;

    scheduleDeferredBootstrap();
  })().catch((error) => {
    console.error('Bootstrap initialize failed', error);
    throw error;
  });

  return initializePromise;
};

const updatePlanInfo = async () => {
  return getPlanInfo();
};

const updateAccountInfo = async () => {
  return getAccountInfo();
};

const updateCompanyTree = async () => {
  return getCompanyTree({ force: true });
};

/** Shared, deduped ensure — call from BreadCrumbs / CompanyTreeList / selectors. */
const ensureCompanyTree = async () => {
  return getCompanyTree({ force: false });
};

let applicantTagsByIdsInflight = null;
let applicantTagsByIdsQueued = new Set();

/**
 * Resolve only the tag ids shown on Pipeline rows. Never pull the full ~2800-tag
 * catalog just to render chips — that pagination + Redux update freezes the UI.
 * Concurrent row mounts share one batched inq request.
 */
async function fetchApplicantTagsByIds(ids) {
  const unique = [];
  const seen = new Set();
  (ids || []).forEach((id) => {
    if (id == null || seen.has(id)) return;
    seen.add(id);
    unique.push(id);
  });
  if (unique.length === 0) return [];

  unique.forEach((id) => {
    if (!applicantTagsById.has(id)) applicantTagsByIdsQueued.add(id);
  });

  if (applicantTagsByIdsQueued.size > 0) {
    if (!applicantTagsByIdsInflight) {
      applicantTagsByIdsInflight = (async () => {
        await new Promise((r) => setTimeout(r, 40));
        const missing = Array.from(applicantTagsByIdsQueued);
        applicantTagsByIdsQueued = new Set();
        for (let i = 0; i < missing.length; i += 400) {
          const chunk = missing.slice(i, i + 400);
          const filter = {
            fields: ['id', 'name', 'active', 'color'],
            where: {
              id: { inq: chunk },
              deleted: false
            },
            limit: chunk.length
          };
          const { status, data } = await apiGet('ApplicantTags', filter);
          if (status >= 200 && Array.isArray(data)) {
            data.forEach((tag) => {
              if (tag && tag.id != null) applicantTagsById.set(tag.id, tag);
            });
          }
        }
      })().finally(() => {
        applicantTagsByIdsInflight = null;
      });
    }
    await applicantTagsByIdsInflight;
  }

  return unique.map((id) => applicantTagsById.get(id)).filter(Boolean);
}

const updateApplicantTags = async () => {
  applicantTagsCache = null;
  applicantTagsById.clear();
  return getApplicantTags({ force: true });
};

/** Shared, deduped ensure — safe to call from many Pipeline rows at once. */
const ensureApplicantTags = async () => {
  return getApplicantTags({ force: false });
};

const updateJobTags = async () => {
  jobTagsCache = null;
  return getJobTags({ force: true });
};

const ensureJobTags = async () => {
  return getJobTags({ force: false });
};

const updateUser = async () => {
  return refreshUser();
};

const updateJobRequests = async () => {
  return getOpenRequestsCount();
};

export {
  initialize,
  updatePlanInfo,
  updateAccountInfo,
  updateCompanyTree,
  ensureCompanyTree,
  updateApplicantTags,
  ensureApplicantTags,
  fetchApplicantTagsByIds,
  updateJobTags,
  ensureJobTags,
  updateUser,
  updateJobRequests
};
